/**
 * Máy chủ giả của module `ocr` (`/api/v1/m/ocr/...`): loại chứng từ, model AI, chứng từ, kho bản OCR.
 *
 * Từ chối **như backend** của `modules/ocr/backend` để nhánh lỗi của màn chạy được khi chưa có
 * module thật: cùng `error_code`, cùng câu `detail`, lỗi ràng buộc kèm `errors[].pointer`.
 * Màu và nhãn không ở đây (`modules/<phân hệ>/constants.ts`): tầng này bị xoá khi có API thật.
 *
 * Tình huống mẫu để soát:
 * - tạo loại chứng từ mã `HDVAT-DAU-VAO` → `409 OCR_SCHEMA_CODE_EXISTS` dưới ô mã;
 * - xoá loại chứng từ đang có chứng từ → `409 OCR_SCHEMA_IN_USE`; `SAO-KE-NH` (tắt, chưa dùng) xoá được;
 * - ô chọn model đọc danh mục hiệu lực của tenant đang đăng nhập (`mock/ai-model.ts`, ADR-0083 mục 4);
 * - chứng từ đang xử lý tự chạy qua các giai đoạn rồi thành "Chờ xác nhận" sau vài giây;
 * - chuyển chứng từ "Biên bản bàn giao kho Thủ Đức" → `403` thao tác nhạy cảm (cần xác thực lại);
 * - kho bản OCR: `hop-dong-thue-van-phong.pdf` đang đọc, `bang-ke-scan-mo.png` đọc lỗi.
 */
import type { ApiError, LoiTruong } from '../lib/errors';
import { useAuthStore } from '../stores/auth';
import type { AiEngine, AiModel, AiModelOption } from '../modules/ai-model/types';
import { modelDungDuoc } from './ai-model';
import type {
  DocumentType,
  OcrColumn,
  OcrColumnInput,
  OcrColumnPatch,
  OcrField,
  OcrFieldInput,
  OcrFieldPatch,
  OcrSchemaCreate,
  OcrSchemaDetail,
  OcrSchemaListItem,
  OcrSchemaPage,
  OcrSchemaPatch,
  OcrSchemaStats,
  OcrTable,
  OcrTableInput,
  OcrTablePatch,
} from '../modules/cau-hinh-ocr/types';
import type {
  BulkResult,
  DocDetail,
  DocJobStatus,
  DocLineItem,
  DocListItem,
  DocPage,
  DocPatch,
  DocReprocessInput,
  DocStats,
  DocStatus,
  DocTranscript,
  DocUploadAccepted,
  DocValue,
  LiveStage,
  OcrStage,
} from '../modules/xu-ly/types';
import type { OcrFile, OcrFilePage, OcrFileState, OcrUploadResult, OcrVersion } from '../modules/kho-ocr/types';

/** Người trong `mock/nguoi-dung.ts`, để cột người tạo tra ra tên. */
const NGUOI = ['u-004', 'u-010', 'u-016', 'u-001'] as const;

let dem = 1000;
const moi = (tienTo: string) => `${tienTo}-${(dem += 1).toString(16).padStart(8, '0')}-mock`;
const bayGio = () => new Date().toISOString();

function loi(status: number, code: string, message: string, details?: LoiTruong[]): ApiError {
  return { status, code, message, ...(details ? { details } : {}) };
}
const khongThay = (cau: string) => loi(404, 'NOT_FOUND', cau);
const xungDotTrangThai = (cau: string) => loi(409, 'OCR_DOCUMENT_STATUS_CONFLICT', cau);

/** Bỏ dấu để tìm không phân biệt dấu, như `ILIKE` trên dữ liệu đã chuẩn hoá. */
function khongDau(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase();
}
const chua = (s: string | null | undefined, q: string) => khongDau(s ?? '').includes(khongDau(q));

/** Phân trang con trỏ: con trỏ là chỉ số dòng kế tiếp, đủ cho dữ liệu mẫu. */
function trang<T>(ds: T[], limit: number, cursor?: string): { items: T[]; next_cursor: string | null } {
  const tu = cursor ? Number(cursor) : 0;
  const items = ds.slice(tu, tu + limit);
  return { items, next_cursor: tu + limit < ds.length ? String(tu + limit) : null };
}

const sao = <T>(x: T): T => structuredClone(x);

// ───────────────────────────── Model AI ─────────────────────────────

/**
 * Danh mục hiệu lực của tenant đang đăng nhập: module ocr không còn giữ danh mục riêng (ADR-0083
 * mục 5), mà đọc danh mục nền tảng đã trừ model bị tắt cho tenant.
 */
const modelHieuLuc = (): AiModel[] => modelDungDuoc(useAuthStore.getState().user?.tenantId);

/** Engine đọc riêng từng trang và engine điền dữ liệu (`ai/engines.ts`). */
const DOC_TRANG: ReadonlySet<AiEngine> = new Set([
  'CHANDRA', 'DEEPSEEK', 'CHANDRA2', 'PADDLE', 'CHANDRA2_L40', 'QWEN38', 'CHANDRA2_H200', 'QWEN38_H200', 'GEMMA4', 'GEMINI',
]);
const DIEN_DU_LIEU: ReadonlySet<AiEngine> = new Set(['GEMINI', 'CLAUDE', 'FOXAI', 'GLM', 'QWEN38', 'QWEN38_H200', 'GEMMA4']);

export const mayChuModelAi = {
  /** `GET /ai-models/options`: model tenant dùng được, kèm vai làm được ở chế độ tách hai bước. */
  luaChon(): { items: AiModelOption[]; next_cursor: null } {
    return {
      items: modelHieuLuc().map((m) => ({
        key: m.key,
        label: m.label,
        engine: m.engine,
        thinking_mode: m.thinking_mode,
        is_default: m.is_default,
        description: m.description,
        can_read_pages: DOC_TRANG.has(m.engine),
        can_map: DIEN_DU_LIEU.has(m.engine),
      })),
      next_cursor: null,
    };
  },
};

// ───────────────────────────── Loại chứng từ ─────────────────────────────

type SchemaLuu = Omit<OcrSchemaDetail, never>;

const truong = (
  field_key: string,
  label: string,
  data_type: OcrField['data_type'],
  position: OcrField['position'],
  display_order: number,
  phu: Partial<OcrField> = {},
): OcrField => ({
  id: `f-${field_key}-${display_order}`,
  field_key,
  label,
  data_type,
  position,
  is_required: false,
  is_unique: false,
  validation_regex: null,
  description: null,
  source_label: null,
  display_order,
  denorm_target: null,
  created_at: '2026-10-21T03:00:00Z',
  updated_at: '2026-10-21T03:00:00Z',
  created_by: 'u-004',
  ...phu,
});

const cot = (column_key: string, label: string, data_type: OcrColumn['data_type'], display_order: number): OcrColumn => ({
  id: `c-${column_key}-${display_order}-${(dem += 1)}`,
  column_key,
  label,
  data_type,
  is_required: column_key === 'ten_hang',
  description: null,
  source_label: null,
  display_order,
  created_at: '2026-10-21T03:00:00Z',
  updated_at: '2026-10-21T03:00:00Z',
  created_by: 'u-004',
});

const bang = (table_key: string, name: string, columns: OcrColumn[], phu: Partial<OcrTable> = {}): OcrTable => ({
  id: `t-${table_key}-${(dem += 1)}`,
  table_key,
  name,
  description: null,
  display_order: 0,
  has_header_row: true,
  columns,
  created_at: '2026-10-21T03:00:00Z',
  updated_at: '2026-10-21T03:00:00Z',
  created_by: 'u-004',
  ...phu,
});

const COT_HANG_HOA = () => [
  cot('ten_hang', 'Tên hàng hóa, dịch vụ', 'TEXT', 0),
  cot('don_vi_tinh', 'Đơn vị tính', 'TEXT', 1),
  cot('so_luong', 'Số lượng', 'NUMBER', 2),
  cot('don_gia', 'Đơn giá', 'CURRENCY', 3),
  cot('thanh_tien', 'Thành tiền', 'CURRENCY', 4),
];

const loaiChungTu = (
  id: string,
  code: string,
  name: string,
  type: DocumentType,
  fields: OcrField[],
  tables: OcrTable[],
  phu: Partial<SchemaLuu> = {},
): SchemaLuu => ({
  id,
  code,
  name,
  type,
  description: null,
  is_active: true,
  split_pdf_pages: true,
  enable_word_boxes: true,
  transform_by_prompt: true,
  model_self_map: true,
  ocr_provider: null,
  extraction_mode: 'SINGLE_MODEL',
  ocr_reader_provider: null,
  created_at: '2026-10-21T03:00:00Z',
  updated_at: '2026-10-21T03:00:00Z',
  created_by: 'u-004',
  fields,
  tables,
  ...phu,
});

const SCHEMAS: SchemaLuu[] = [
  loaiChungTu(
    's-hdvat',
    'HDVAT-DAU-VAO',
    'Hóa đơn GTGT đầu vào',
    'INVOICE',
    [
      truong('so_hoa_don', 'Số hóa đơn', 'TEXT', 'HEADER', 0, { is_required: true, denorm_target: 'INVOICE_NUMBER', source_label: 'Số|Số hóa đơn' }),
      truong('ky_hieu', 'Ký hiệu', 'TEXT', 'HEADER', 1),
      truong('ngay_lap', 'Ngày lập', 'DATE', 'HEADER', 2, { is_required: true, denorm_target: 'ISSUE_DATE' }),
      truong('ten_nguoi_ban', 'Tên người bán', 'TEXT', 'HEADER', 3, { denorm_target: 'SELLER_NAME', source_label: 'Đơn vị bán hàng' }),
      truong('mst_nguoi_ban', 'MST người bán', 'TEXT', 'HEADER', 4, { denorm_target: 'SELLER_TAX_CODE', validation_regex: '^[0-9]{10}(-[0-9]{3})?$' }),
      truong('ten_nguoi_mua', 'Tên người mua', 'TEXT', 'HEADER', 5),
      truong('tien_truoc_thue', 'Tiền trước thuế', 'CURRENCY', 'FOOTER', 6, { denorm_target: 'TOTAL_AMOUNT' }),
      truong('thue_gtgt', 'Thuế GTGT', 'CURRENCY', 'FOOTER', 7, { denorm_target: 'VAT_AMOUNT' }),
      truong('tong_thanh_toan', 'Tổng thanh toán', 'CURRENCY', 'FOOTER', 8, { is_required: true, denorm_target: 'GRAND_TOTAL' }),
      truong('da_thanh_toan', 'Đã thanh toán', 'BOOLEAN', 'FOOTER', 9),
    ],
    [bang('hang_hoa', 'Bảng hàng hóa, dịch vụ', COT_HANG_HOA())],
    {
      description: 'Hóa đơn giá trị gia tăng nhận từ nhà cung cấp. Số tiền ghi theo đồng, bỏ dấu phân cách.',
      extraction_mode: 'OCR_THEN_MAP',
      ocr_provider: 'gemini',
      ocr_reader_provider: 'chandra2-ocr',
    },
  ),
  loaiChungTu(
    's-hdmb',
    'HD-MUA-BAN',
    'Hợp đồng mua bán hàng hóa',
    'CONTRACT',
    [
      truong('so_hop_dong', 'Số hợp đồng', 'TEXT', 'HEADER', 0, { is_required: true, denorm_target: 'INVOICE_NUMBER' }),
      truong('ngay_ky', 'Ngày ký', 'DATE', 'HEADER', 1, { denorm_target: 'ISSUE_DATE' }),
      truong('ben_ban', 'Bên bán', 'TEXT', 'BODY', 2, { denorm_target: 'SELLER_NAME' }),
      truong('ben_mua', 'Bên mua', 'TEXT', 'BODY', 3),
      truong('gia_tri', 'Giá trị hợp đồng', 'CURRENCY', 'FOOTER', 4, { denorm_target: 'GRAND_TOTAL' }),
      truong('hinh_thuc_tt', 'Hình thức thanh toán', 'LIST', 'FOOTER', 5, { description: 'Chuyển khoản | Tiền mặt | Trả góp' }),
    ],
    [bang('hang_hoa', 'Danh mục hàng hóa', COT_HANG_HOA())],
    { ocr_provider: 'claude', created_by: 'u-010' },
  ),
  loaiChungTu(
    's-pnk',
    'PNK-VAT-TU',
    'Phiếu nhập kho vật tư',
    'WAREHOUSE_RECEIPT',
    [
      truong('so_phieu', 'Số phiếu', 'TEXT', 'HEADER', 0, { is_required: true, denorm_target: 'INVOICE_NUMBER' }),
      truong('ngay_nhap', 'Ngày nhập', 'DATE', 'HEADER', 1, { denorm_target: 'ISSUE_DATE' }),
      truong('kho_nhap', 'Kho nhập', 'TEXT', 'HEADER', 2),
      truong('nguoi_giao', 'Người giao hàng', 'TEXT', 'BODY', 3),
    ],
    [
      bang('vat_tu', 'Vật tư nhập kho', [
        cot('ma_vat_tu', 'Mã vật tư', 'TEXT', 0),
        cot('ten_hang', 'Tên vật tư', 'TEXT', 1),
        cot('so_luong', 'Số lượng', 'NUMBER', 2),
      ]),
      bang('chi_phi', 'Chi phí vận chuyển', [cot('noi_dung', 'Nội dung', 'TEXT', 0), cot('so_tien', 'Số tiền', 'CURRENCY', 1)], {
        display_order: 1,
        has_header_row: false,
      }),
    ],
  ),
  loaiChungTu(
    's-bbbg',
    'BB-BAN-GIAO',
    'Biên bản bàn giao tài sản',
    'MINUTES',
    [
      truong('so_bien_ban', 'Số biên bản', 'TEXT', 'HEADER', 0),
      truong('ngay_ban_giao', 'Ngày bàn giao', 'DATE', 'HEADER', 1),
      truong('ben_giao', 'Bên giao', 'TEXT', 'BODY', 2),
      truong('ben_nhan', 'Bên nhận', 'TEXT', 'BODY', 3),
    ],
    [],
    { created_by: 'u-016' },
  ),
  loaiChungTu('s-saoke', 'SAO-KE-NH', 'Sao kê tài khoản ngân hàng', 'STATEMENT', [], [], {
    is_active: false,
    created_at: '2026-10-22T04:00:00Z',
    updated_at: '2026-10-22T04:00:00Z',
  }),
];

function schemaTheoId(id: string): SchemaLuu {
  const s = SCHEMAS.find((x) => x.id === id);
  if (!s) throw khongThay('Không tìm thấy loại chứng từ.');
  return s;
}

function soChungTu(schemaId: string): number {
  return DOCS.filter((d) => d.schema_id === schemaId).length;
}

/** `assertSchemaModels` của backend: model phải có trong danh mục, đang bật, và làm được đúng vai. */
function kiemModelCuaSchema(du: { extraction_mode?: string; ocr_provider?: string | null; ocr_reader_provider?: string | null }): void {
  const errors: LoiTruong[] = [];
  const ktra = (key: string | null | undefined, pointer: string, vai: 'reader' | 'mapper') => {
    if (!key) return;
    const m = modelHieuLuc().find((x) => x.key === key);
    if (!m) errors.push({ pointer, detail: `Model AI "${key}" không dùng được: đã tắt hoặc không còn trong danh mục.` });
    else if (vai === 'reader' && !DOC_TRANG.has(m.engine)) {
      errors.push({ pointer, detail: `Model AI "${m.label}" không đọc riêng từng trang được.` });
    } else if (vai === 'mapper' && !DIEN_DU_LIEU.has(m.engine)) {
      errors.push({ pointer, detail: `Model AI "${m.label}" là model đọc chữ chuyên dụng, không điền được dữ liệu vào trường/bảng.` });
    }
  };
  ktra(du.ocr_provider, '#/ocr_provider', 'mapper');
  if (du.extraction_mode === 'OCR_THEN_MAP') {
    if (!du.ocr_reader_provider) errors.push({ pointer: '#/ocr_reader_provider', detail: 'Chế độ tách hai bước cần model đọc chữ.' });
    ktra(du.ocr_reader_provider, '#/ocr_reader_provider', 'reader');
  }
  if (errors.length) throw loi(400, 'INVALID_ARGUMENT', 'Model AI của loại chứng từ chưa dùng được.', errors);
}

const KHOA_TRUONG = /^[a-zA-Z][a-zA-Z0-9_]*$/;

function kiemTruong(du: Partial<OcrFieldInput>, s: SchemaLuu, boQuaId?: string): void {
  const errors: LoiTruong[] = [];
  if (du.field_key !== undefined) {
    if (!KHOA_TRUONG.test(du.field_key)) {
      errors.push({ pointer: '#/field_key', detail: 'Chỉ gồm chữ cái, số và dấu gạch dưới, bắt đầu bằng chữ cái.' });
    } else if (s.fields.some((f) => f.field_key === du.field_key && f.id !== boQuaId)) {
      throw loi(409, 'OCR_SCHEMA_KEY_EXISTS', `Trường "${du.field_key}" đã có trong loại chứng từ này.`, [
        { pointer: '#/field_key', detail: 'Khoá này đã được dùng.' },
      ]);
    }
  }
  if (du.label !== undefined && !du.label.trim()) errors.push({ pointer: '#/label', detail: 'Không được để trống.' });
  if (du.validation_regex) {
    try {
      new RegExp(du.validation_regex);
    } catch {
      errors.push({ pointer: '#/validation_regex', detail: 'Biểu thức chính quy không hợp lệ.' });
    }
  }
  if (du.denorm_target) {
    const khac = s.fields.find((f) => f.denorm_target === du.denorm_target && f.id !== boQuaId);
    if (khac) errors.push({ pointer: '#/denorm_target', detail: `Trường "${khac.label}" đã gán vào cột lọc này.` });
  }
  if (errors.length) throw loi(400, 'INVALID_ARGUMENT', 'Thông tin trường chưa đúng, vui lòng sửa các trường được đánh dấu.', errors);
}

function demDanhSach(s: SchemaLuu): OcrSchemaListItem {
  const { fields, tables, ...goc } = s;
  return { ...sao(goc), field_count: fields.length, table_count: tables.length, document_count: soChungTu(s.id) };
}

export const mayChuLoaiChungTu = {
  danhSach({ search, type, is_active, limit, cursor }: {
    search?: string; type?: DocumentType; is_active?: boolean; limit: number; cursor?: string;
  }): OcrSchemaPage {
    let ds = [...SCHEMAS].sort((a, b) => b.created_at.localeCompare(a.created_at));
    if (type) ds = ds.filter((s) => s.type === type);
    if (is_active !== undefined) ds = ds.filter((s) => s.is_active === is_active);
    if (search) ds = ds.filter((s) => chua(s.code, search) || chua(s.name, search) || chua(s.description, search));
    const t = trang(ds, limit, cursor);
    return { items: t.items.map(demDanhSach), next_cursor: t.next_cursor };
  },
  thongKe(): OcrSchemaStats {
    return {
      total_schemas: SCHEMAS.length,
      active_schemas: SCHEMAS.filter((s) => s.is_active).length,
      total_fields: SCHEMAS.reduce((n, s) => n + s.fields.length, 0),
      total_tables: SCHEMAS.reduce((n, s) => n + s.tables.length, 0),
    };
  },
  chiTiet: (id: string): OcrSchemaDetail => sao(schemaTheoId(id)),
  theoMa(code: string): OcrSchemaDetail {
    const s = SCHEMAS.find((x) => x.code === code);
    if (!s) throw khongThay('Không tìm thấy loại chứng từ.');
    return sao(s);
  },
  tao(du: OcrSchemaCreate): OcrSchemaDetail {
    const errors: LoiTruong[] = [];
    if (!/^[A-Z0-9_-]+$/.test(du.code)) errors.push({ pointer: '#/code', detail: 'Mã chứng từ chỉ gồm chữ hoa, số, _ và -.' });
    if (!du.name.trim()) errors.push({ pointer: '#/name', detail: 'Không được để trống.' });
    if (errors.length) throw loi(400, 'INVALID_ARGUMENT', 'Thông tin loại chứng từ chưa đúng, vui lòng sửa các trường được đánh dấu.', errors);
    kiemModelCuaSchema(du);
    if (SCHEMAS.some((s) => s.code === du.code)) {
      throw loi(409, 'OCR_SCHEMA_CODE_EXISTS', `Mã chứng từ "${du.code}" đã có, vui lòng chọn mã khác.`, [
        { pointer: '#/code', detail: 'Mã này đã được dùng.' },
      ]);
    }
    const s = loaiChungTu(moi('s'), du.code, du.name.trim(), du.type, [], [], {
      description: du.description ?? null,
      ocr_provider: du.ocr_provider ?? null,
      extraction_mode: du.extraction_mode ?? 'SINGLE_MODEL',
      ocr_reader_provider: du.ocr_reader_provider ?? null,
      created_at: bayGio(),
      updated_at: bayGio(),
      created_by: 'u-001',
    });
    SCHEMAS.push(s);
    return sao(s);
  },
  sua(id: string, du: OcrSchemaPatch): OcrSchemaDetail {
    const s = schemaTheoId(id);
    if (du.name !== undefined && !du.name.trim()) {
      throw loi(400, 'INVALID_ARGUMENT', 'Thông tin loại chứng từ chưa đúng, vui lòng sửa các trường được đánh dấu.', [
        { pointer: '#/name', detail: 'Không được để trống.' },
      ]);
    }
    if (du.extraction_mode !== undefined || du.ocr_provider !== undefined || du.ocr_reader_provider !== undefined) {
      kiemModelCuaSchema({
        extraction_mode: du.extraction_mode ?? s.extraction_mode,
        ocr_provider: du.ocr_provider !== undefined ? du.ocr_provider : s.ocr_provider,
        ocr_reader_provider: du.ocr_reader_provider !== undefined ? du.ocr_reader_provider : s.ocr_reader_provider,
      });
    }
    Object.assign(s, du, { updated_at: bayGio() });
    return sao(s);
  },
  xoa(id: string): void {
    const s = schemaTheoId(id);
    const n = soChungTu(id);
    if (n > 0) {
      throw loi(409, 'OCR_SCHEMA_IN_USE',
        `Không xoá được: loại chứng từ đang gắn với ${n} chứng từ đã xử lý. Hãy gỡ hoặc xoá các chứng từ đó trước.`);
    }
    SCHEMAS.splice(SCHEMAS.indexOf(s), 1);
  },
  themTruong(id: string, du: OcrFieldInput): OcrField {
    const s = schemaTheoId(id);
    kiemTruong(du, s);
    const f = truong(du.field_key, du.label.trim(), du.data_type, du.position, du.display_order ?? s.fields.length, {
      ...du,
      id: moi('f'),
      created_at: bayGio(),
      updated_at: bayGio(),
      created_by: 'u-001',
    });
    s.fields.push(f);
    return sao(f);
  },
  suaTruong(id: string, fieldId: string, du: OcrFieldPatch): OcrField {
    const s = schemaTheoId(id);
    const f = s.fields.find((x) => x.id === fieldId);
    if (!f) throw khongThay('Không tìm thấy trường trong loại chứng từ này.');
    kiemTruong(du, s, fieldId);
    Object.assign(f, du, { updated_at: bayGio() });
    return sao(f);
  },
  xoaTruong(id: string, fieldId: string): void {
    const s = schemaTheoId(id);
    const f = s.fields.find((x) => x.id === fieldId);
    if (!f) throw khongThay('Không tìm thấy trường trong loại chứng từ này.');
    const coGiaTri = DOCS.some((d) => d.schema_id === id && d.values.some((v) => v.field_id === fieldId && v.string_value));
    if (coGiaTri) {
      throw loi(409, 'OCR_SCHEMA_IN_USE', `Không xoá được trường "${f.label}": đã có chứng từ mang giá trị của trường này.`);
    }
    s.fields.splice(s.fields.indexOf(f), 1);
  },
  themBang(id: string, du: OcrTableInput): OcrTable {
    const s = schemaTheoId(id);
    if (!KHOA_TRUONG.test(du.table_key)) {
      throw loi(400, 'INVALID_ARGUMENT', 'Thông tin bảng chưa đúng, vui lòng sửa các trường được đánh dấu.', [
        { pointer: '#/table_key', detail: 'Chỉ gồm chữ cái, số và dấu gạch dưới, bắt đầu bằng chữ cái.' },
      ]);
    }
    if (s.tables.some((t) => t.table_key === du.table_key)) {
      throw loi(409, 'OCR_SCHEMA_KEY_EXISTS', `Bảng "${du.table_key}" đã có trong loại chứng từ này.`, [
        { pointer: '#/table_key', detail: 'Khoá này đã được dùng.' },
      ]);
    }
    const t = bang(du.table_key, du.name.trim(), [], {
      id: moi('t'),
      description: du.description ?? null,
      has_header_row: du.has_header_row ?? true,
      display_order: s.tables.length,
    });
    s.tables.push(t);
    return sao(t);
  },
  suaBang(id: string, tableId: string, du: OcrTablePatch): OcrTable {
    const t = bangTheoId(id, tableId);
    if (du.table_key && du.table_key !== t.table_key && DOCS.some((d) => d.line_items.some((l) => l.table_key === t.table_key))) {
      throw loi(409, 'OCR_SCHEMA_IN_USE', `Không đổi được khoá bảng "${t.table_key}": đã có chứng từ mang dòng của bảng này.`);
    }
    Object.assign(t, du, { updated_at: bayGio() });
    return sao(t);
  },
  xoaBang(id: string, tableId: string): void {
    const s = schemaTheoId(id);
    const t = bangTheoId(id, tableId);
    if (DOCS.some((d) => d.schema_id === id && d.line_items.some((l) => (l.table_key ?? s.tables[0]?.table_key) === t.table_key))) {
      throw loi(409, 'OCR_SCHEMA_IN_USE', `Không xoá được bảng "${t.name}": đã có chứng từ mang dòng của bảng này.`);
    }
    s.tables.splice(s.tables.indexOf(t), 1);
  },
  themCot(id: string, tableId: string, du: OcrColumnInput): OcrColumn {
    const t = bangTheoId(id, tableId);
    if (t.columns.some((c) => c.column_key === du.column_key)) {
      throw loi(409, 'OCR_SCHEMA_KEY_EXISTS', `Cột "${du.column_key}" đã có trong bảng này.`, [
        { pointer: '#/column_key', detail: 'Khoá này đã được dùng.' },
      ]);
    }
    const c = { ...cot(du.column_key, du.label.trim(), du.data_type, du.display_order ?? t.columns.length), ...du, id: moi('c') };
    t.columns.push(c as OcrColumn);
    return sao(c as OcrColumn);
  },
  suaCot(id: string, tableId: string, columnId: string, du: OcrColumnPatch): OcrColumn {
    const t = bangTheoId(id, tableId);
    const c = t.columns.find((x) => x.id === columnId);
    if (!c) throw khongThay('Không tìm thấy cột trong loại chứng từ này.');
    if (du.column_key && du.column_key !== c.column_key && t.columns.some((x) => x.column_key === du.column_key)) {
      throw loi(409, 'OCR_SCHEMA_KEY_EXISTS', `Cột "${du.column_key}" đã có trong bảng này.`, [
        { pointer: '#/column_key', detail: 'Khoá này đã được dùng.' },
      ]);
    }
    Object.assign(c, du, { updated_at: bayGio() });
    return sao(c);
  },
  xoaCot(id: string, tableId: string, columnId: string): void {
    const t = bangTheoId(id, tableId);
    const c = t.columns.find((x) => x.id === columnId);
    if (!c) throw khongThay('Không tìm thấy cột trong loại chứng từ này.');
    t.columns.splice(t.columns.indexOf(c), 1);
  },
};

function bangTheoId(id: string, tableId: string): OcrTable {
  const t = schemaTheoId(id).tables.find((x) => x.id === tableId);
  if (!t) throw khongThay('Không tìm thấy bảng trong loại chứng từ này.');
  return t;
}

// ───────────────────────────── Chứng từ ─────────────────────────────

type DocLuu = Omit<DocDetail, 'schema'> & { tienDo: number };

const TEP = [
  ['hoa-don-0012458.pdf', 'application/pdf'],
  ['bang-ke-thang-9.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  ['hop-dong-cung-cap-thiet-bi-2026.docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  ['phieu-nhap-kho-thang-10.pdf', 'application/pdf'],
  ['bien-ban-ban-giao-thiet-bi-van-phong-chi-nhanh-mien-nam-dot-2.pdf', 'application/pdf'],
  ['anh-chup-hoa-don.jpg', 'image/jpeg'],
] as const;

const NGUOI_BAN = [
  ['Công ty TNHH Thương mại Đại Phát', '0312345678'],
  ['Công ty Cổ phần Thiết bị Minh Long', '0109876543'],
  ['Công ty TNHH Vật tư Xây dựng An Khang', '0401234567'],
] as const;

/** Kế hoạch trạng thái: mỗi nhãn, mỗi giai đoạn đều có ít nhất một dòng. */
const KE_HOACH: ReadonlyArray<readonly [DocStatus, OcrStage | null]> = [
  ['PROCESSING', 'QUEUED'],
  ['PROCESSED', 'DONE'],
  ['CONFIRMED', 'DONE'],
  ['PROCESSING', 'READING'],
  ['PROCESSED', 'DONE'],
  ['TRANSFERRED', 'DONE'],
  ['PROCESSING', 'READ_DONE'],
  ['ERROR', 'FAILED'],
  ['CONFIRMED', 'DONE'],
  ['PROCESSED', 'DONE'],
  ['PROCESSING', 'MAPPING'],
  ['TRANSFERRED', 'DONE'],
  ['DRAFT', 'DONE'],
  ['PROCESSING', 'RETRY_WAIT'],
  ['CONFIRMED', 'DONE'],
  ['PROCESSED', 'DONE'],
  ['TRANSFERRED', 'DONE'],
];

function giaTriMau(s: SchemaLuu, i: number): DocValue[] {
  const [ban, mst] = NGUOI_BAN[i % NGUOI_BAN.length]!;
  const tien = 1_000_000 * (12 + i * 7);
  const mauTheoKhoa: Record<string, string> = {
    so_hoa_don: String(12458 + i).padStart(8, '0'),
    ky_hieu: '1C26TAA',
    ngay_lap: `2026-10-${String(1 + (i % 20)).padStart(2, '0')}`,
    ten_nguoi_ban: ban,
    mst_nguoi_ban: mst,
    ten_nguoi_mua: 'Công ty Cổ phần Khách hàng Việt',
    tien_truoc_thue: String(tien),
    thue_gtgt: String(tien / 10),
    tong_thanh_toan: String(tien + tien / 10),
    da_thanh_toan: i % 2 ? 'true' : 'false',
    so_hop_dong: `HĐ-${120 + i}/2026`,
    ngay_ky: `2026-09-${String(1 + (i % 28)).padStart(2, '0')}`,
    ben_ban: ban,
    ben_mua: 'Công ty Cổ phần Khách hàng Việt',
    gia_tri: String(tien * 3),
    hinh_thuc_tt: 'Chuyển khoản',
    so_phieu: `PNK-${300 + i}`,
    ngay_nhap: `2026-10-${String(2 + (i % 20)).padStart(2, '0')}`,
    kho_nhap: 'Kho Thủ Đức',
    nguoi_giao: 'Trần Văn Giao',
    so_bien_ban: `BB-${40 + i}`,
    ngay_ban_giao: `2026-10-${String(3 + (i % 20)).padStart(2, '0')}`,
    ben_giao: ban,
    ben_nhan: 'Phòng Hành chính',
  };
  return s.fields.map((f, j) => ({
    id: `v-${i}-${j}`,
    field_id: f.id,
    // Trường thứ hai của dòng lẻ để trống: soát ô chưa nhận dạng được.
    string_value: i % 5 === 1 && j === 1 ? null : (mauTheoKhoa[f.field_key] ?? null),
    page_number: 1,
    source_text: null,
    confidence: 0.82 + ((i + j) % 15) / 100,
    is_manually_edited: false,
    field: { field_key: f.field_key, label: f.label, data_type: f.data_type, display_order: f.display_order },
  }));
}

function dongMau(s: SchemaLuu, i: number): DocLineItem[] {
  const ra: DocLineItem[] = [];
  for (const t of s.tables) {
    const so = t.table_key === 'chi_phi' ? 1 : 3 + (i % 3);
    for (let k = 0; k < so; k++) {
      const extra: Record<string, unknown> = {};
      for (const c of t.columns) {
        extra[c.column_key] =
          c.data_type === 'NUMBER' ? 2 + k * 3
            : c.data_type === 'CURRENCY' ? (1_250_000 + k * 375_000) * (c.column_key === 'thanh_tien' ? 2 + k * 3 : 1)
              : c.column_key === 'ten_hang'
                ? ['Máy tính để bàn Dell OptiPlex 7010', 'Màn hình LCD 24 inch', 'Bàn làm việc gỗ công nghiệp phủ melamine chống ẩm', 'Ghế xoay lưới lưng cao', 'Ổ cứng SSD 1 TB'][k % 5]
                : c.column_key === 'don_vi_tinh' ? 'Chiếc'
                  : c.column_key === 'ma_vat_tu' ? `VT-${1000 + k}`
                    : 'Phí vận chuyển nội thành';
      }
      ra.push({
        id: `li-${i}-${t.table_key}-${k}`,
        stt: k + 1,
        table_key: t.table_key,
        name: null,
        unit: null,
        quantity: null,
        unit_price: null,
        amount: null,
        extra_data: extra,
        is_manually_added: false,
      });
    }
  }
  return ra;
}

/** Câu lỗi đúng khuôn backend: lỗi thường, hết hạn mức (`han-muc.ts`), không đủ hạn mức theo quy tắc (`cau.go`). */
const LOI_MAU = [
  'Model không trả về JSON hợp lệ sau 3 lần thử. Thử quét lại hoặc đổi model suy luận.',
  'Tổ chức đã hết hạn mức token AI tháng này (đã dùng 1.000.000 / 1.000.000 token). Các chức năng dùng AI tạm dừng tới 00:00 ngày 01/12/2026; các chức năng khác vẫn dùng bình thường. Liên hệ quản trị nền tảng nếu cần nâng hạn mức.',
  'Yêu cầu này cần 12.480 token cho phần nội dung gửi đi, hạn mức AI tổ chức đặt cho phòng ban của bạn tháng này chỉ còn 500 token. Gửi ít nội dung hơn hoặc liên hệ quản trị viên của tổ chức.',
];

function taoChungTu(): DocLuu[] {
  const ra: DocLuu[] = [];
  const dung = SCHEMAS.filter((s) => s.is_active);
  for (let i = 0; i < 34; i++) {
    const s = dung[i % dung.length]!;
    const [status, stage] = KE_HOACH[i % KE_HOACH.length]!;
    const [file_name, mime_type] = TEP[i % TEP.length]!;
    const coKetQua = !['PROCESSING', 'ERROR'].includes(status) || stage === 'MAPPING';
    const values = coKetQua ? giaTriMau(s, i) : [];
    const theo = (k: string) => values.find((v) => v.field.field_key === k)?.string_value ?? null;
    const tao = `2026-10-${String(22 - (i % 20)).padStart(2, '0')}T0${i % 10}:${String((i * 7) % 60).padStart(2, '0')}:00Z`;
    ra.push({
      id: `doc-${String(i + 1).padStart(3, '0')}`,
      schema_id: s.id,
      schema_code: s.code,
      file_name: i === 19 ? 'Biên bản bàn giao kho Thủ Đức.pdf' : file_name,
      file_size: 180_000 + i * 53_211,
      mime_type,
      extra_files: i % 7 === 3 ? [{ index: 0, file_name: 'phu-luc-1.pdf', mime_type: 'application/pdf' }] : [],
      invoice_number: theo('so_hoa_don') ?? theo('so_hop_dong') ?? theo('so_phieu'),
      issue_date: theo('ngay_lap') ?? theo('ngay_ky') ?? theo('ngay_nhap'),
      seller_tax_code: theo('mst_nguoi_ban'),
      seller_name: theo('ten_nguoi_ban') ?? theo('ben_ban'),
      total_amount: theo('tien_truoc_thue'),
      vat_amount: theo('thue_gtgt'),
      grand_total: theo('tong_thanh_toan') ?? theo('gia_tri'),
      status,
      ocr_confidence: coKetQua ? 0.86 + (i % 12) / 100 : null,
      ocr_language: 'vi',
      ocr_engine_version: coKetQua ? 'gemini-2.5-flash' : null,
      ocr_error: status === 'ERROR'
        ? LOI_MAU[i % LOI_MAU.length]!
        : stage === 'RETRY_WAIT' ? 'Nhà cung cấp AI trả 429, chờ thử lại.' : null,
      ocr_stage: stage,
      ocr_stage_at: tao,
      ocr_attempt: stage === 'RETRY_WAIT' ? 2 : null,
      ocr_error_step: status === 'ERROR' ? 'MAP' : null,
      ocr_queued_at: tao,
      ocr_started_at: tao,
      ocr_read_at: coKetQua ? tao : null,
      ocr_finished_at: coKetQua && status !== 'PROCESSING' ? tao : null,
      confirmed_by: ['CONFIRMED', 'TRANSFERRED'].includes(status) ? 'u-010' : null,
      confirmed_at: ['CONFIRMED', 'TRANSFERRED'].includes(status) ? tao : null,
      processed_by: status === 'TRANSFERRED' ? 'u-010' : null,
      processed_at: status === 'TRANSFERRED' ? tao : null,
      created_at: tao,
      updated_at: tao,
      created_by: NGUOI[i % NGUOI.length]!,
      has_transcript: status !== 'PROCESSING' || stage === 'READ_DONE' || stage === 'MAPPING',
      values,
      line_items: coKetQua ? dongMau(s, i) : [],
      tienDo: 0,
    });
  }
  return ra;
}

const DOCS: DocLuu[] = taoChungTu();

const THU_TU_GIAI_DOAN: OcrStage[] = ['QUEUED', 'READING', 'READ_DONE', 'MAPPING'];
/**
 * Chứng từ vừa tải lên hay vừa quét lại chạy thật qua bốn giai đoạn, mỗi lần đọc nhích một
 * bậc, rồi thành "Chờ xác nhận" — đủ để soát nhãn giai đoạn và việc màn tự tải lại.
 */
function nhichTienDo(d: DocLuu): void {
  if (d.status !== 'PROCESSING' || d.tienDo === 0) return;
  d.tienDo += 1;
  if (d.tienDo <= THU_TU_GIAI_DOAN.length) {
    d.ocr_stage = THU_TU_GIAI_DOAN[d.tienDo - 1]!;
    if (d.ocr_stage === 'READ_DONE') d.has_transcript = true;
    return;
  }
  const s = schemaTheoId(d.schema_id);
  d.status = 'PROCESSED';
  d.ocr_stage = 'DONE';
  d.ocr_finished_at = bayGio();
  d.ocr_confidence = 0.93;
  d.values = giaTriMau(s, 3);
  d.line_items = dongMau(s, 3);
  d.tienDo = 0;
}

function docTheoId(id: string): DocLuu {
  const d = DOCS.find((x) => x.id === id);
  if (!d) throw khongThay('Không tìm thấy chứng từ.');
  return d;
}

function dongDanhSach(d: DocLuu): DocListItem {
  const s = SCHEMAS.find((x) => x.id === d.schema_id);
  const { values: _v, line_items: _l, tienDo: _t, ...goc } = d;
  return {
    ...sao(goc),
    schema: {
      name: s?.name ?? d.schema_code,
      type: s?.type ?? 'OTHERS',
      ocr_provider: s?.ocr_provider ?? null,
      ocr_reader_provider: s?.ocr_reader_provider ?? null,
      extraction_mode: s?.extraction_mode ?? 'SINGLE_MODEL',
    },
  };
}

function batDauLuot(d: DocLuu, dungBanChep: boolean): void {
  d.status = 'PROCESSING';
  d.ocr_error = null;
  d.ocr_error_step = null;
  d.ocr_stage = dungBanChep ? 'READ_DONE' : 'QUEUED';
  d.tienDo = dungBanChep ? 3 : 1;
  d.ocr_queued_at = bayGio();
  d.updated_at = bayGio();
}

const SUA_DUOC: readonly DocStatus[] = ['DRAFT', 'PROCESSED'];
const XOA_DUOC: readonly DocStatus[] = ['DRAFT', 'PROCESSING', 'PROCESSED', 'ERROR'];
const QUET_LAI_DUOC: readonly DocStatus[] = ['DRAFT', 'PROCESSED', 'ERROR'];

export const mayChuChungTu = {
  danhSach({ search, status, ocr_stage, type, limit, cursor }: {
    search?: string; status?: DocStatus[]; ocr_stage?: OcrStage[]; type?: DocumentType; limit: number; cursor?: string;
  }): DocPage {
    DOCS.forEach(nhichTienDo);
    let ds = [...DOCS].sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id));
    if (status?.length) ds = ds.filter((d) => status.includes(d.status));
    if (ocr_stage?.length) ds = ds.filter((d) => d.ocr_stage !== null && ocr_stage.includes(d.ocr_stage));
    if (type) ds = ds.filter((d) => SCHEMAS.find((s) => s.id === d.schema_id)?.type === type);
    if (search) {
      ds = ds.filter((d) => {
        const s = SCHEMAS.find((x) => x.id === d.schema_id);
        return [d.id, d.invoice_number, d.seller_name, d.file_name, d.schema_code, s?.name].some((x) => chua(x, search));
      });
    }
    const t = trang(ds, limit, cursor);
    return { items: t.items.map(dongDanhSach), next_cursor: t.next_cursor };
  },
  thongKe(): DocStats {
    const theo = (s: DocStatus) => DOCS.filter((d) => d.status === s).length;
    const giaiDoan = (g: LiveStage) => DOCS.filter((d) => d.status === 'PROCESSING' && d.ocr_stage === g).length;
    return {
      total: DOCS.length,
      draft: theo('DRAFT'),
      processing: theo('PROCESSING'),
      confirmed: theo('CONFIRMED'),
      processed: theo('PROCESSED'),
      transferred: theo('TRANSFERRED'),
      error: theo('ERROR'),
      processing_by_stage: {
        QUEUED: giaiDoan('QUEUED'),
        READING: giaiDoan('READING'),
        READ_DONE: giaiDoan('READ_DONE'),
        MAPPING: giaiDoan('MAPPING'),
        RETRY_WAIT: giaiDoan('RETRY_WAIT'),
        UNKNOWN: 0,
      },
    };
  },
  chiTiet(id: string): DocDetail {
    const d = docTheoId(id);
    nhichTienDo(d);
    const { tienDo: _t, ...goc } = d;
    return { ...sao(goc), schema: sao(schemaTheoId(d.schema_id)) };
  },
  trangThaiJob(id: string): DocJobStatus {
    const d = docTheoId(id);
    nhichTienDo(d);
    return {
      document_id: id,
      status: d.status,
      ocr_stage: d.ocr_stage,
      job: { state: d.status === 'PROCESSING' ? 'active' : 'completed', progress: null, failed_reason: null, attempt: 1, max_attempts: 3, queue_position: null },
    };
  },
  banChep(id: string): DocTranscript {
    const d = docTheoId(id);
    if (!d.has_transcript) return { document_id: id, page_count: 0, files: [] };
    const s = schemaTheoId(d.schema_id);
    const dong = d.values.map((v) => `${v.field.label}: ${v.string_value ?? ''}`).join('\n');
    return {
      document_id: id,
      page_count: 2,
      files: [
        {
          file_index: 0,
          file_name: d.file_name,
          mime_type: d.mime_type,
          reader_label: 'Chandra 2 (OCR)',
          is_text_layer: d.mime_type === 'application/pdf' && Number(d.id.slice(-1)) % 2 === 0,
          transcribed_at: d.ocr_read_at,
          ocr_transcript_id: `tr-${d.id}`,
          reused: false,
          pages: [
            { page_number: 1, content: `# ${s.name.toUpperCase()}\n\n${dong}` },
            { page_number: 2, content: 'Người lập phiếu (ký, ghi rõ họ tên)\n\nKế toán trưởng (ký, ghi rõ họ tên)' },
          ],
        },
      ],
    };
  },
  taiLen(du: { schema_id: string; soTep: number; tenTep: string; mime: string; kichThuoc: number }): DocUploadAccepted {
    const s = SCHEMAS.find((x) => x.id === du.schema_id);
    if (!s) {
      throw loi(400, 'INVALID_ARGUMENT', 'Thông tin chưa đúng.', [{ pointer: '#/schema_id', detail: 'Không tìm thấy loại chứng từ.' }]);
    }
    if (!s.is_active) throw loi(400, 'INVALID_ARGUMENT', `Loại chứng từ "${s.code}" đã bị tắt, hãy bật lại trước khi quét.`);
    const d: DocLuu = {
      ...taoChungTu()[0]!,
      id: moi('doc'),
      schema_id: s.id,
      schema_code: s.code,
      file_name: du.tenTep,
      mime_type: du.mime,
      file_size: du.kichThuoc,
      extra_files: [],
      values: [],
      line_items: [],
      has_transcript: false,
      created_at: bayGio(),
      updated_at: bayGio(),
      created_by: 'u-001',
      confirmed_by: null,
      confirmed_at: null,
      processed_by: null,
      processed_at: null,
      ocr_confidence: null,
    };
    batDauLuot(d, false);
    DOCS.unshift(d);
    return { document_id: d.id, job_id: `t.mock.doc.${d.id}.1`, status: 'QUEUED', message: `Đã tải ${du.soTep} tệp và đẩy vào hàng đợi nhận dạng.` };
  },
  quetLai(id: string, du: DocReprocessInput): DocDetail {
    const d = docTheoId(id);
    if (!QUET_LAI_DUOC.includes(d.status)) {
      throw xungDotTrangThai('Chỉ quét lại được chứng từ đang ở trạng thái Nháp, Đã xử lý hoặc Lỗi.');
    }
    if (du.reuse_transcript && !d.has_transcript) {
      throw loi(400, 'INVALID_ARGUMENT', 'Chứng từ chưa có bản chép OCR để dùng lại — hãy quét lại từ đầu.', [
        { pointer: '#/reuse_transcript', detail: 'Chưa có bản chép.' },
      ]);
    }
    batDauLuot(d, Boolean(du.reuse_transcript || (du.from_failed_step && d.ocr_error_step === 'MAP' && d.has_transcript)));
    return mayChuChungTu.chiTiet(id);
  },
  huyQuet(id: string): DocDetail {
    const d = docTheoId(id);
    if (d.status !== 'PROCESSING') throw xungDotTrangThai('Chứng từ không còn đang quét — không có lượt quét nào để huỷ.');
    d.status = 'ERROR';
    d.ocr_error_step = d.ocr_stage === 'MAPPING' || d.ocr_stage === 'READ_DONE' ? 'MAP' : 'READ';
    d.ocr_stage = 'FAILED';
    d.ocr_error = 'Người dùng đã huỷ lượt quét.';
    d.tienDo = 0;
    return mayChuChungTu.chiTiet(id);
  },
  sua(id: string, du: DocPatch): DocDetail {
    const d = docTheoId(id);
    if (!SUA_DUOC.includes(d.status)) throw xungDotTrangThai('Chỉ sửa được chứng từ ở trạng thái Nháp hoặc Đã xử lý.');
    for (const v of du.values ?? []) {
      const cu = d.values.find((x) => x.field_id === v.field_id);
      if (cu) Object.assign(cu, { string_value: v.string_value, is_manually_edited: true });
      else {
        const f = schemaTheoId(d.schema_id).fields.find((x) => x.id === v.field_id);
        if (!f) {
          throw loi(400, 'INVALID_ARGUMENT', 'Thông tin chưa đúng.', [{ pointer: '#/values/0/field_id', detail: 'Trường không thuộc loại chứng từ.' }]);
        }
        d.values.push({
          id: moi('v'), field_id: f.id, string_value: v.string_value, page_number: null, source_text: null, confidence: null,
          is_manually_edited: true, field: { field_key: f.field_key, label: f.label, data_type: f.data_type, display_order: f.display_order },
        });
      }
    }
    if (du.status === 'DRAFT' && d.status === 'PROCESSED') d.status = 'DRAFT';
    d.updated_at = bayGio();
    return mayChuChungTu.chiTiet(id);
  },
  xacNhan(id: string): DocDetail {
    const d = docTheoId(id);
    if (!SUA_DUOC.includes(d.status)) throw xungDotTrangThai('Chỉ chứng từ Nháp hoặc Đã xử lý mới xác nhận được.');
    Object.assign(d, { status: 'CONFIRMED', confirmed_by: 'u-001', confirmed_at: bayGio(), updated_at: bayGio() });
    return mayChuChungTu.chiTiet(id);
  },
  chuyen(id: string): DocDetail {
    const d = docTheoId(id);
    if (d.file_name === 'Biên bản bàn giao kho Thủ Đức.pdf') {
      throw loi(403, 'STEP_UP_REQUIRED', 'Thao tác nhạy cảm cần xác thực lại. Đăng nhập lại rồi thử lại trong 5 phút.');
    }
    if (d.status !== 'CONFIRMED') throw xungDotTrangThai('Chỉ chứng từ "Đã xác nhận" mới chuyển được vào kho tri thức.');
    Object.assign(d, { status: 'TRANSFERRED', processed_by: 'u-001', processed_at: bayGio(), updated_at: bayGio() });
    return mayChuChungTu.chiTiet(id);
  },
  xoa(id: string): void {
    const d = docTheoId(id);
    if (!XOA_DUOC.includes(d.status)) throw xungDotTrangThai('Chỉ xoá được chứng từ chưa xác nhận và chưa chuyển.');
    DOCS.splice(DOCS.indexOf(d), 1);
  },
  hangLoat(viec: 'xac-nhan' | 'chuyen' | 'xoa', ids: string[]): BulkResult {
    const xong: string[] = [];
    for (const id of ids) {
      const d = DOCS.find((x) => x.id === id);
      if (!d) continue;
      if (viec === 'xac-nhan' && SUA_DUOC.includes(d.status)) {
        Object.assign(d, { status: 'CONFIRMED', confirmed_by: 'u-001', confirmed_at: bayGio() });
        xong.push(id);
      } else if (viec === 'chuyen' && d.status === 'CONFIRMED') {
        Object.assign(d, { status: 'TRANSFERRED', processed_by: 'u-001', processed_at: bayGio() });
        xong.push(id);
      } else if (viec === 'xoa' && XOA_DUOC.includes(d.status)) {
        DOCS.splice(DOCS.indexOf(d), 1);
        xong.push(id);
      }
    }
    const skipped = ids.filter((x) => !xong.includes(x));
    if (viec === 'xac-nhan') return { confirmed: xong.length, skipped };
    if (viec === 'chuyen') return { transferred: xong.length, skipped };
    return { deleted: xong.length, skipped };
  },
  /** Nội dung xem trước: bản dev của module trả thẳng tệp. Dữ liệu mẫu dựng một trang văn bản. */
  tep(id: string): { blob: Blob; mime_type: string; file_name: string } {
    const d = docTheoId(id);
    const dong = [d.schema_code, d.file_name ?? '', ...d.values.map((v) => `${v.field.label}: ${v.string_value ?? ''}`)];
    return { blob: new Blob([dong.join('\n')], { type: 'text/plain' }), mime_type: 'text/plain', file_name: d.file_name ?? '' };
  },
};

// ───────────────────────────── Kho bản OCR ─────────────────────────────

const banOcr = (id: string, status: OcrVersion['status'], phu: Partial<OcrVersion> = {}): OcrVersion => ({
  id,
  status,
  origin: 'PRE_OCR',
  reader_key: 'chandra2-ocr',
  provider: 'CHANDRA2',
  model_label: 'Chandra 2 (OCR)',
  page_count: 3,
  pages_done: status === 'DONE' ? 3 : status === 'READING' ? 1 : 0,
  error: status === 'FAILED' ? 'Endpoint đọc chữ không trả lời sau 120 giây.' : null,
  pinned_at: null,
  created_by: 'u-004',
  created_at: '2026-10-20T02:10:00Z',
  started_at: '2026-10-20T02:10:05Z',
  finished_at: status === 'DONE' || status === 'FAILED' ? '2026-10-20T02:11:40Z' : null,
  origin_document_id: null,
  reader_label: 'Chandra 2 (OCR)',
  is_text_layer: false,
  attached_count: 0,
  is_effective: false,
  delete_blocked_reason: null,
  documents: [],
  ...phu,
});

interface TepLuu {
  id: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  created_by: string;
  created_at: string;
  versions: OcrVersion[];
}

const KHO: TepLuu[] = [
  {
    id: 'sf-001', file_name: 'hoa-don-0012458.pdf', file_size: 412_330, mime_type: 'application/pdf', created_by: 'u-004',
    created_at: '2026-10-22T02:00:00Z',
    versions: [
      banOcr('tv-001b', 'DONE', { created_at: '2026-10-22T02:20:00Z', reader_label: 'Lớp văn bản PDF', provider: 'pdf-text-layer', is_text_layer: true, reader_key: null }),
      banOcr('tv-001a', 'DONE', {
        created_at: '2026-10-22T02:00:00Z', origin: 'DOCUMENT', pinned_at: '2026-10-22T02:30:00Z', attached_count: 2,
        documents: [
          { id: 'doc-001', file_name: 'hoa-don-0012458.pdf', schema_code: 'HDVAT-DAU-VAO', schema_name: 'Hóa đơn GTGT đầu vào', status: 'PROCESSED', created_at: '2026-10-22T02:00:00Z' },
          { id: 'doc-011', file_name: 'hoa-don-0012458.pdf', schema_code: 'HDVAT-DAU-VAO', schema_name: 'Hóa đơn GTGT đầu vào', status: 'CONFIRMED', created_at: '2026-10-21T02:00:00Z' },
        ],
      }),
    ],
  },
  {
    id: 'sf-002', file_name: 'hop-dong-thue-van-phong.pdf', file_size: 1_843_002, mime_type: 'application/pdf', created_by: 'u-010',
    created_at: '2026-10-22T05:00:00Z',
    versions: [banOcr('tv-002a', 'READING', { page_count: 12, pages_done: 5, created_by: 'u-010' })],
  },
  {
    id: 'sf-003', file_name: 'bang-ke-scan-mo.png', file_size: 845_120, mime_type: 'image/png', created_by: 'u-016',
    created_at: '2026-10-21T07:30:00Z',
    versions: [banOcr('tv-003a', 'FAILED', { page_count: 1, created_by: 'u-016' })],
  },
  {
    id: 'sf-004', file_name: 'phieu-nhap-kho-thang-10.pdf', file_size: 290_442, mime_type: 'application/pdf', created_by: 'u-004',
    created_at: '2026-10-20T09:00:00Z', versions: [banOcr('tv-004a', 'DONE', { reader_key: 'qwen3.8-27b', provider: 'QWEN38', model_label: 'Qwen 3.8 27B', reader_label: 'Qwen 3.8 27B' })],
  },
  {
    id: 'sf-005', file_name: 'bien-ban-ban-giao-thiet-bi-van-phong-chi-nhanh-mien-nam-dot-2-nam-2026.pdf', file_size: 3_120_400,
    mime_type: 'application/pdf', created_by: 'u-001', created_at: '2026-10-19T03:00:00Z', versions: [],
  },
  {
    id: 'sf-006', file_name: 'anh-chup-hoa-don.jpg', file_size: 512_000, mime_type: 'image/jpeg', created_by: 'u-010',
    created_at: '2026-10-18T03:00:00Z',
    versions: [banOcr('tv-006a', 'DONE', { page_count: 1, attached_count: 1, documents: [
      { id: 'doc-006', file_name: 'anh-chup-hoa-don.jpg', schema_code: 'HDVAT-DAU-VAO', schema_name: 'Hóa đơn GTGT đầu vào', status: 'TRANSFERRED', created_at: '2026-10-18T03:00:00Z' },
    ] })],
  },
];

const DANG_DOC = (v: OcrVersion) => v.status === 'QUEUED' || v.status === 'READING';

function trangThaiTep(vs: OcrVersion[]): OcrFileState {
  if (vs.some(DANG_DOC)) return 'RUNNING';
  if (vs.some((v) => v.status === 'DONE')) return 'READY';
  return vs.length ? 'FAILED' : 'EMPTY';
}

/** Dựng lại các trường suy ra (`views` của backend): bản dùng được, lý do chưa xoá được. */
function xemTep(f: TepLuu): OcrFile {
  const xong = f.versions.filter((v) => v.status === 'DONE');
  xong.sort((a, b) => (b.pinned_at ?? '').localeCompare(a.pinned_at ?? '') || b.created_at.localeCompare(a.created_at));
  const hieuLuc = xong[0]?.id ?? null;
  const versions = f.versions.map((v) => ({
    ...v,
    is_effective: v.id === hieuLuc,
    delete_blocked_reason: DANG_DOC(v)
      ? 'Bản OCR đang được đọc — huỷ lượt đọc trước khi xoá.'
      : v.attached_count > 0 ? `Bản OCR đang dùng cho ${v.attached_count} chứng từ — xoá các chứng từ đó trước.` : null,
  }));
  const gan = versions.reduce((n, v) => n + v.attached_count, 0);
  return sao({
    ...f,
    state: trangThaiTep(versions),
    version_count: versions.length,
    attached_count: gan,
    effective: versions.find((v) => v.is_effective) ?? null,
    running: versions.find(DANG_DOC) ?? null,
    delete_blocked_reason: versions.some(DANG_DOC)
      ? 'Tài liệu đang được đọc — huỷ lượt đọc trước khi xoá.'
      : gan > 0 ? `Tài liệu có bản OCR đang dùng cho ${gan} chứng từ — xoá các chứng từ đó trước.` : null,
    versions,
  });
}

function tepTheoId(id: string): TepLuu {
  const f = KHO.find((x) => x.id === id);
  if (!f) throw khongThay('Không tìm thấy tài liệu trong kho bản OCR.');
  return f;
}

function banTheoId(f: TepLuu, versionId: string): OcrVersion {
  const v = f.versions.find((x) => x.id === versionId);
  if (!v) throw khongThay('Không tìm thấy bản OCR của tài liệu này.');
  return v;
}

const xungDotBan = (cau: string) => loi(409, 'OCR_TRANSCRIPT_CONFLICT', cau);

/** Bản đang đọc nhích một trang mỗi lần tra, đọc hết thì xong. */
function nhichBan(f: TepLuu): void {
  for (const v of f.versions) {
    if (!DANG_DOC(v)) continue;
    v.status = 'READING';
    v.pages_done = Math.min((v.pages_done ?? 0) + 1, v.page_count ?? 1);
    if (v.pages_done >= (v.page_count ?? 1)) Object.assign(v, { status: 'DONE', finished_at: bayGio() });
  }
}

export const mayChuKhoOcr = {
  danhSach({ search, state, limit, cursor }: { search?: string; state?: OcrFileState; limit: number; cursor?: string }): OcrFilePage {
    let ds = KHO.map(xemTep).sort((a, b) => b.created_at.localeCompare(a.created_at));
    if (state) ds = ds.filter((f) => f.state === state);
    if (search) ds = ds.filter((f) => chua(f.file_name, search));
    const t = trang(ds, limit, cursor);
    // Danh sách không kèm chứng từ của từng bản, như backend.
    return { items: t.items.map((f) => ({ ...f, versions: f.versions.map(({ documents: _d, ...v }) => v) })), next_cursor: t.next_cursor };
  },
  chiTiet(id: string): OcrFile {
    const f = tepTheoId(id);
    nhichBan(f);
    return xemTep(f);
  },
  taiLen(tep: Array<{ ten: string; mime: string; kichThuoc: number }>, reader_key: string, skip_if_done: boolean): OcrUploadResult {
    const m = modelHieuLuc().find((x) => x.key === reader_key);
    if (!m || !DOC_TRANG.has(m.engine)) {
      throw loi(400, 'INVALID_ARGUMENT', 'Thông tin chưa đúng.', [{ pointer: '#/reader_key', detail: 'Chọn một model đọc chữ đang bật.' }]);
    }
    return {
      results: tep.map((t) => {
        if (!/^(application\/pdf|image\/)/.test(t.mime)) {
          return { file_name: t.ten, outcome: 'REJECTED' as const, message: 'Chỉ đọc trước được tệp PDF hoặc ảnh.' };
        }
        const cu = KHO.find((f) => f.file_name === t.ten);
        if (cu && cu.versions.some(DANG_DOC)) return { file_name: t.ten, outcome: 'SKIPPED_RUNNING' as const, source_file_id: cu.id };
        if (cu && skip_if_done && cu.versions.some((v) => v.status === 'DONE')) {
          return { file_name: t.ten, outcome: 'SKIPPED_DONE' as const, source_file_id: cu.id };
        }
        const f = cu ?? { id: moi('sf'), file_name: t.ten, file_size: t.kichThuoc, mime_type: t.mime, created_by: 'u-001', created_at: bayGio(), versions: [] };
        if (!cu) KHO.unshift(f);
        const v = banOcr(moi('tv'), 'QUEUED', { reader_key: m.key, provider: m.engine, model_label: m.label, reader_label: m.label, created_at: bayGio(), created_by: 'u-001', page_count: 2 });
        f.versions.unshift(v);
        return { file_name: t.ten, outcome: 'QUEUED' as const, source_file_id: f.id, version_id: v.id };
      }),
    };
  },
  docLai(id: string, reader_key: string): OcrFile {
    const f = tepTheoId(id);
    if (f.versions.some(DANG_DOC)) throw xungDotBan('Tài liệu đang được đọc — đợi lượt hiện tại xong hoặc huỷ nó trước.');
    const m = modelHieuLuc().find((x) => x.key === reader_key);
    if (!m) throw loi(400, 'INVALID_ARGUMENT', 'Thông tin chưa đúng.', [{ pointer: '#/reader_key', detail: 'Chọn một model đọc chữ đang bật.' }]);
    f.versions.unshift(banOcr(moi('tv'), 'QUEUED', { reader_key: m.key, provider: m.engine, model_label: m.label, reader_label: m.label, created_at: bayGio(), created_by: 'u-001' }));
    return xemTep(f);
  },
  ghim(id: string, versionId: string): OcrFile {
    const f = tepTheoId(id);
    const v = banTheoId(f, versionId);
    if (v.status !== 'DONE') throw xungDotBan('Chỉ ghim được bản OCR đã đọc xong.');
    f.versions.forEach((x) => (x.pinned_at = null));
    v.pinned_at = bayGio();
    return xemTep(f);
  },
  boGhim(id: string, versionId: string): OcrFile {
    const f = tepTheoId(id);
    banTheoId(f, versionId).pinned_at = null;
    return xemTep(f);
  },
  huyDoc(id: string, versionId: string): OcrFile {
    const f = tepTheoId(id);
    const v = banTheoId(f, versionId);
    if (!DANG_DOC(v)) throw xungDotBan('Bản OCR không còn đang đọc — không có gì để huỷ.');
    Object.assign(v, { status: 'FAILED', error: 'Người dùng đã huỷ lượt đọc.', finished_at: bayGio() });
    return xemTep(f);
  },
  xoaBan(id: string, versionId: string): void {
    const f = tepTheoId(id);
    const v = xemTep(f).versions.find((x) => x.id === versionId);
    if (!v) throw khongThay('Không tìm thấy bản OCR của tài liệu này.');
    if (v.delete_blocked_reason) throw xungDotBan(v.delete_blocked_reason);
    f.versions = f.versions.filter((x) => x.id !== versionId);
  },
  xoaTep(id: string): void {
    const f = tepTheoId(id);
    const xem = xemTep(f);
    if (xem.delete_blocked_reason) throw xungDotBan(xem.delete_blocked_reason);
    KHO.splice(KHO.indexOf(f), 1);
  },
  banChep(id: string, versionId: string): DocTranscript {
    const f = tepTheoId(id);
    const v = banTheoId(f, versionId);
    if (v.status !== 'DONE') throw xungDotBan('Bản OCR chưa đọc xong.');
    const pages = Array.from({ length: v.page_count ?? 1 }, (_x, i) => ({
      page_number: i + 1,
      content: i === 0 ? `# ${f.file_name}\n\nCỘNG HOÀ XÃ HỘI CHỦ NGHĨA VIỆT NAM\nĐộc lập – Tự do – Hạnh phúc` : `Trang ${i + 1}: nội dung đã đọc.`,
    }));
    return {
      source_file_id: id,
      page_count: pages.length,
      files: [{
        file_index: 0, file_name: f.file_name, mime_type: f.mime_type, reader_label: v.reader_label, is_text_layer: v.is_text_layer,
        transcribed_at: v.finished_at, ocr_transcript_id: v.id, reused: false, pages,
      }],
    };
  },
  tep(id: string): { blob: Blob; mime_type: string; file_name: string } {
    const f = tepTheoId(id);
    return { blob: new Blob([f.file_name], { type: 'text/plain' }), mime_type: 'text/plain', file_name: f.file_name };
  },
};
